// pages/company/SubmitProblem.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  User,
  FileText,
  DollarSign,
  TrendingUp,
  Shield,
  CheckCircle,
  Upload,
  Save,
  ChevronRight,
  ChevronLeft,
  Info,
  AlertCircle,
  Briefcase,
  Mail,
  Phone,
  Calendar,
  Clock,
  Scale,
  Cpu,
  FileSignature,
  X,
  Building2,
  Database,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { submissionService, SubmissionData } from '@/lib/submissionService';
import { demoCompanyService } from '@/lib/demoCompanyService';
import { demoProblemGenerator } from '@/lib/demoProblemGenerator';

// Step configuration - STARTING FROM STEP 2 (Contact Person)
const STEPS = [
  {
    number: 2,
    title: 'Contact Person',
    description: 'Primary and secondary contacts',
    icon: User,
    color: 'green'
  },
  {
    number: 3,
    title: 'Problem Description',
    description: 'Detailed problem analysis',
    icon: FileText,
    color: 'purple'
  },
  {
    number: 4,
    title: 'Financial Impact',
    description: 'Grey cost quantification',
    icon: DollarSign,
    color: 'red'
  },
  {
    number: 5,
    title: 'Market Prevalence',
    description: 'Scalability assessment',
    icon: TrendingUp,
    color: 'orange'
  },
  {
    number: 6,
    title: 'Pilot Readiness',
    description: 'Commercial intent and readiness',
    icon: Briefcase,
    color: 'indigo'
  },
  {
    number: 7,
    title: 'Technical Environment',
    description: 'Facility infrastructure',
    icon: Cpu,
    color: 'pink'
  },
  {
    number: 8,
    title: 'Regulatory Context',
    description: 'Compliance and certifications',
    icon: Shield,
    color: 'teal'
  },
  {
    number: 9,
    title: 'Self-Assessment',
    description: 'Submitter scoring',
    icon: Scale,
    color: 'cyan'
  },
  {
    number: 10,
    title: 'Consent & Submit',
    description: 'Data consent and declaration',
    icon: FileSignature,
    color: 'emerald'
  }
];

// Problem categories from VV-DOC-006
const PROBLEM_CATEGORIES = [
  { value: 'energy_loss', label: 'Energy loss / inefficiency' },
  { value: 'downtime', label: 'Machine downtime / maintenance failure' },
  { value: 'water_loss', label: 'Water loss / waste' },
  { value: 'inventory_shrinkage', label: 'Inventory shrinkage / spoilage' },
  { value: 'process_inefficiency', label: 'Process inefficiency / yield loss' },
  { value: 'quality_control', label: 'Quality control failure / rework' },
  { value: 'cold_chain', label: 'Cold chain / temperature breach' },
  { value: 'logistics_delay', label: 'Logistics delay / asset underutilisation' },
  { value: 'compliance', label: 'Compliance / regulatory gap' },
  { value: 'data_blindness', label: 'Data blindness / lack of monitoring' },
  { value: 'labour_productivity', label: 'Labour productivity loss' },
  { value: 'other', label: 'Other' }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  'Continuous (happening right now, every day)',
  'Recurring daily',
  'Recurring weekly',
  'Recurring monthly',
  'Seasonal',
  'Episodic but unpredictable'
];

// Problem scope options
const SCOPE_OPTIONS = [
  'Affects a single machine or asset',
  'Affects a production line',
  'Affects an entire department',
  'Affects the whole facility',
  'Affects multiple facilities in our network'
];

// Evidence options
const EVIDENCE_OPTIONS = [
  'Electricity bills (last 12 months)',
  'Water / utilities bills (last 12 months)',
  'Machine maintenance and downtime logs',
  'Production yield and waste records',
  'Inventory shrinkage or spoilage records',
  'Quality control and rework reports',
  'Labour time and overtime records',
  'ERP or MES system data exports',
  'Previous consultant or audit reports',
  'Insurance claims related to the problem',
  'Photographs or video of the problem',
  'Equipment specifications and manuals'
];

// Industry prevalence options
const INDUSTRY_PREVALENCE_OPTIONS = [
  'I believe it is unique to our facility',
  'I have heard of similar problems at other facilities in our sector',
  'I know of multiple competitors / peers who face the same problem',
  'This is widely understood to be an industry-wide problem',
  'I do not know'
];

// Pilot likelihood options
const PILOT_LIKELIHOOD_OPTIONS = [
  'Very likely — we would pilot immediately',
  'Likely — subject to internal approval',
  'Possible — depends on the solution',
  'Unlikely — submitting for awareness only',
  'Unsure at this point'
];

// Approval authority options
const APPROVAL_AUTHORITY_OPTIONS = [
  'Operations Manager (I am the approver)',
  'Plant / Factory Manager',
  'Chief Operations Officer',
  'Chief Executive Officer / MD',
  'Board of Directors',
  'Parent company / international HQ approval required'
];

// Commercial model options
const COMMERCIAL_MODEL_OPTIONS = [
  'One-time purchase',
  'Annual subscription',
  'Pay-from-savings',
  'Open to discussion'
];

// Connectivity options
const CONNECTIVITY_OPTIONS = [
  'Reliable fibre broadband',
  'Mobile data (4G/3G) only',
  'Limited / intermittent connectivity',
  'No internet connectivity at the site',
  'Secure corporate network — external devices must be approved'
];

export default function CompanySubmit() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Check mode and load company info
    const demo = submissionService.isDemoMode();
    setIsDemoMode(demo);
    
    const company = submissionService.getCompanyInfo();
    setCompanyInfo(company);
  }, []);

  // Check if we should auto-generate a demo problem
  useEffect(() => {
    const shouldGenerate = searchParams.get('demo') === 'true';
    const isDemo = submissionService.isDemoMode();
    
    if (shouldGenerate && isDemo) {
      generateDemoProblem();
    }
  }, [searchParams]);

  const generateDemoProblem = () => {
    const company = demoCompanyService.getCurrentCompany();
    if (!company) {
      toast({
        title: 'Error',
        description: 'No company found for demo generation',
        variant: 'destructive'
      });
      return;
    }

    // Generate a random demo problem
    const demoProblem = demoProblemGenerator.generateProblem(company);
    
    // Map the generated data to form state
    setFormData({
      // Contact Information
      contactName: demoProblem.contactName,
      contactTitle: demoProblem.contactTitle,
      contactEmail: demoProblem.contactEmail,
      contactPhone: demoProblem.contactPhone,
      contactDepartment: demoProblem.contactDepartment || '',
      yearsInRole: demoProblem.yearsInRole || '',
      decisionMaker: demoProblem.decisionMaker || '',
      decisionMakerDetails: demoProblem.decisionMakerDetails || '',
      secondaryName: demoProblem.secondaryName || '',
      secondaryTitle: demoProblem.secondaryTitle || '',
      secondaryEmail: demoProblem.secondaryEmail || '',
      secondaryPhone: demoProblem.secondaryPhone || '',

      // Problem Details
      problemTitle: demoProblem.problemTitle,
      problemCategory: demoProblem.problemCategory,
      otherCategory: demoProblem.otherCategory || '',
      plainDescription: demoProblem.plainDescription,
      rootCause: demoProblem.rootCause || '',
      firstAppeared: demoProblem.firstAppeared || '',
      frequency: demoProblem.frequency || '',
      seasonalMonths: demoProblem.seasonalMonths || '',
      scope: demoProblem.scope || '',
      previousAttempts: demoProblem.previousAttempts || '',

      // Financial Impact
      costEnergy: demoProblem.costEnergy?.toString() || '',
      costMaterial: demoProblem.costMaterial?.toString() || '',
      costDowntime: demoProblem.costDowntime?.toString() || '',
      costLabour: demoProblem.costLabour?.toString() || '',
      costCompliance: demoProblem.costCompliance?.toString() || '',
      costOther: demoProblem.costOther?.toString() || '',
      costOtherDescription: demoProblem.costOtherDescription || '',
      totalAnnualCost: demoProblem.totalAnnualCost?.toString() || '',
      evidence: demoProblem.evidence || [],
      otherEvidence: demoProblem.otherEvidence || '',
      confidenceLevel: demoProblem.confidenceLevel || '',
      estimateDerivation: demoProblem.estimateDerivation || '',

      // Scalability
      industryPrevalence: demoProblem.industryPrevalence || '',
      estimatedKenya: demoProblem.estimatedKenya?.toString() || '',
      estimatedEastAfrica: demoProblem.estimatedEastAfrica?.toString() || '',
      affectedIndustries: demoProblem.affectedIndustries || '',
      scalabilityFactor: demoProblem.scalabilityFactor || '',
      existingSolutions: demoProblem.existingSolutions || '',
      soughtHelp: demoProblem.soughtHelp || [],

      // Pilot Readiness
      pilotLikelihood: demoProblem.pilotLikelihood || '',
      approvalAuthority: demoProblem.approvalAuthority || '',
      approvalTimeline: demoProblem.approvalTimeline || '',
      pilotDuration: demoProblem.pilotDuration || '',
      successCriteria: demoProblem.successCriteria || '',
      budgetRange: demoProblem.budgetRange || '',
      commercialModel: demoProblem.commercialModel || '',
      pilotAccess: demoProblem.pilotAccess || [],

      // Technical Environment
      facilityAge: demoProblem.facilityAge || '',
      facilitySize: demoProblem.facilitySize || '',
      operatingHours: demoProblem.operatingHours || '',
      operatingDays: demoProblem.operatingDays || '',
      monitoringSystems: demoProblem.monitoringSystems || [],
      connectivity: demoProblem.connectivity || '',
      primaryEquipment: demoProblem.primaryEquipment || '',
      equipmentBrand: demoProblem.equipmentBrand || '',
      equipmentAge: demoProblem.equipmentAge || '',
      maintenanceSpend: demoProblem.maintenanceSpend?.toString() || '',
      lastOverhaul: demoProblem.lastOverhaul || '',
      solutionType: demoProblem.solutionType || [],
      technicalConstraints: demoProblem.technicalConstraints || '',

      // Regulatory Context
      regulatoryRelated: demoProblem.regulatoryRelated || '',
      regulatoryBodies: demoProblem.regulatoryBodies || '',
      certifications: demoProblem.certifications || [],
      upcomingDeadlines: demoProblem.upcomingDeadlines || '',

      // Self-Assessment
      severityRating: demoProblem.severityRating,
      urgencyRating: demoProblem.urgencyRating,
      prevalenceRating: demoProblem.prevalenceRating,
      dataAvailabilityRating: demoProblem.dataAvailabilityRating,
      techOpennessRating: demoProblem.techOpennessRating,
      managementSupportRating: demoProblem.managementSupportRating,
      decisionSpeedRating: demoProblem.decisionSpeedRating,
      additionalContext: demoProblem.additionalContext || '',

      // Consent
      consentStorage: demoProblem.consentStorage,
      consentContact: demoProblem.consentContact,
      consentAnonymised: demoProblem.consentAnonymised,
      consentUpdates: demoProblem.consentUpdates,
      consentSquad: demoProblem.consentSquad,
      dataSharingLevel: demoProblem.dataSharingLevel,
      declarationAgreed: demoProblem.declarationAgreed,
    });

    toast({
      title: 'Demo Problem Generated',
      description: 'A realistic demo problem has been created. You can edit it before submitting.',
    });

    // Scroll to top
    window.scrollTo(0, 0);
  };

  // Form data starting from Section 2 (Contact Person)
  const [formData, setFormData] = useState({
    // Section 2: Primary Contact Person
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    contactDepartment: '',
    yearsInRole: '',
    decisionMaker: '',
    decisionMakerDetails: '',
    preferredCommunication: '',
    secondaryName: '',
    secondaryTitle: '',
    secondaryEmail: '',
    secondaryPhone: '',

    // Section 3: Problem Description
    problemTitle: '',
    problemCategory: '',
    otherCategory: '',
    plainDescription: '',
    rootCause: '',
    firstAppeared: '',
    frequency: '',
    seasonalMonths: '',
    scope: '',
    previousAttempts: '',

    // Section 4: Financial Impact
    costEnergy: '',
    costMaterial: '',
    costDowntime: '',
    costLabour: '',
    costCompliance: '',
    costOther: '',
    costOtherDescription: '',
    totalAnnualCost: '',
    evidence: [] as string[],
    otherEvidence: '',
    confidenceLevel: '',
    estimateDerivation: '',

    // Section 5: Scalability
    industryPrevalence: '',
    estimatedKenya: '',
    estimatedEastAfrica: '',
    affectedIndustries: '',
    scalabilityFactor: '',
    existingSolutions: '',
    soughtHelp: [] as string[],

    // Section 6: Pilot Readiness
    pilotLikelihood: '',
    approvalAuthority: '',
    approvalTimeline: '',
    pilotDuration: '',
    successCriteria: '',
    budgetRange: '',
    commercialModel: '',
    pilotAccess: [] as string[],

    // Section 7: Technical Environment
    facilityAge: '',
    facilitySize: '',
    operatingHours: '',
    operatingDays: '',
    monitoringSystems: [] as string[],
    connectivity: '',
    primaryEquipment: '',
    equipmentBrand: '',
    equipmentAge: '',
    maintenanceSpend: '',
    lastOverhaul: '',
    solutionType: [] as string[],
    technicalConstraints: '',

    // Section 8: Regulatory Context
    regulatoryRelated: '',
    regulatoryBodies: '',
    certifications: [] as string[],
    upcomingDeadlines: '',

    // Section 9: Self-Assessment (1-5 scales)
    severityRating: '3',
    urgencyRating: '3',
    prevalenceRating: '3',
    dataAvailabilityRating: '3',
    techOpennessRating: '3',
    managementSupportRating: '3',
    decisionSpeedRating: '3',
    additionalContext: '',

    // Section 10: Consent
    consentStorage: false,
    consentContact: false,
    consentAnonymised: false,
    consentUpdates: false,
    consentSquad: false,
    dataSharingLevel: '',
    declarationAgreed: false
  });

  const getStepIcon = (step: typeof STEPS[0]) => {
    const Icon = step.icon;
    const isCompleted = step.number < currentStep;
    const isCurrent = step.number === currentStep;

    return (
      <div className={`p-2 rounded-full ${
        isCompleted ? 'bg-green-100' :
        isCurrent ? 'bg-blue-100' :
        'bg-gray-100'
      }`}>
        <Icon className={`h-4 w-4 ${
          isCompleted ? 'text-green-600' :
          isCurrent ? 'text-blue-600' :
          'text-gray-400'
        }`} />
      </div>
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadedFiles([...uploadedFiles, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (currentStep < 10) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const calculateTotalCost = () => {
    const total = (
      (parseInt(formData.costEnergy) || 0) +
      (parseInt(formData.costMaterial) || 0) +
      (parseInt(formData.costDowntime) || 0) +
      (parseInt(formData.costLabour) || 0) +
      (parseInt(formData.costCompliance) || 0) +
      (parseInt(formData.costOther) || 0)
    );
    setFormData({...formData, totalAnnualCost: total.toString()});
  };

  useEffect(() => {
    calculateTotalCost();
  }, [
    formData.costEnergy,
    formData.costMaterial,
    formData.costDowntime,
    formData.costLabour,
    formData.costCompliance,
    formData.costOther
  ]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData: SubmissionData = {
        // Contact Information
        contactName: formData.contactName,
        contactTitle: formData.contactTitle,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        contactDepartment: formData.contactDepartment,
        yearsInRole: formData.yearsInRole,
        decisionMaker: formData.decisionMaker,
        decisionMakerDetails: formData.decisionMakerDetails,
        secondaryName: formData.secondaryName,
        secondaryTitle: formData.secondaryTitle,
        secondaryEmail: formData.secondaryEmail,
        secondaryPhone: formData.secondaryPhone,

        // Problem Details
        problemTitle: formData.problemTitle,
        problemCategory: formData.problemCategory,
        otherCategory: formData.otherCategory,
        plainDescription: formData.plainDescription,
        rootCause: formData.rootCause,
        firstAppeared: formData.firstAppeared,
        frequency: formData.frequency,
        seasonalMonths: formData.seasonalMonths,
        scope: formData.scope,
        previousAttempts: formData.previousAttempts,

        // Financial Impact
        costEnergy: parseInt(formData.costEnergy) || undefined,
        costMaterial: parseInt(formData.costMaterial) || undefined,
        costDowntime: parseInt(formData.costDowntime) || undefined,
        costLabour: parseInt(formData.costLabour) || undefined,
        costCompliance: parseInt(formData.costCompliance) || undefined,
        costOther: parseInt(formData.costOther) || undefined,
        costOtherDescription: formData.costOtherDescription,
        totalAnnualCost: parseInt(formData.totalAnnualCost) || 0,
        evidence: formData.evidence,
        otherEvidence: formData.otherEvidence,
        confidenceLevel: formData.confidenceLevel,
        estimateDerivation: formData.estimateDerivation,

        // Scalability
        industryPrevalence: formData.industryPrevalence,
        estimatedKenya: parseInt(formData.estimatedKenya) || undefined,
        estimatedEastAfrica: parseInt(formData.estimatedEastAfrica) || undefined,
        affectedIndustries: formData.affectedIndustries,
        scalabilityFactor: formData.scalabilityFactor,
        existingSolutions: formData.existingSolutions,
        soughtHelp: formData.soughtHelp,

        // Pilot Readiness
        pilotLikelihood: formData.pilotLikelihood,
        approvalAuthority: formData.approvalAuthority,
        approvalTimeline: formData.approvalTimeline,
        pilotDuration: formData.pilotDuration,
        successCriteria: formData.successCriteria,
        budgetRange: formData.budgetRange,
        commercialModel: formData.commercialModel,
        pilotAccess: formData.pilotAccess,

        // Technical Environment
        facilityAge: formData.facilityAge,
        facilitySize: formData.facilitySize,
        operatingHours: formData.operatingHours,
        operatingDays: formData.operatingDays,
        monitoringSystems: formData.monitoringSystems,
        connectivity: formData.connectivity,
        primaryEquipment: formData.primaryEquipment,
        equipmentBrand: formData.equipmentBrand,
        equipmentAge: formData.equipmentAge,
        maintenanceSpend: parseInt(formData.maintenanceSpend) || undefined,
        lastOverhaul: formData.lastOverhaul,
        solutionType: formData.solutionType,
        technicalConstraints: formData.technicalConstraints,

        // Regulatory Context
        regulatoryRelated: formData.regulatoryRelated,
        regulatoryBodies: formData.regulatoryBodies,
        certifications: formData.certifications,
        upcomingDeadlines: formData.upcomingDeadlines,

        // Self-Assessment
        severityRating: formData.severityRating,
        urgencyRating: formData.urgencyRating,
        prevalenceRating: formData.prevalenceRating,
        dataAvailabilityRating: formData.dataAvailabilityRating,
        techOpennessRating: formData.techOpennessRating,
        managementSupportRating: formData.managementSupportRating,
        decisionSpeedRating: formData.decisionSpeedRating,
        additionalContext: formData.additionalContext,

        // Consent
        consentStorage: formData.consentStorage,
        consentContact: formData.consentContact,
        consentAnonymised: formData.consentAnonymised,
        consentUpdates: formData.consentUpdates,
        consentSquad: formData.consentSquad,
        dataSharingLevel: formData.dataSharingLevel,
        declarationAgreed: formData.declarationAgreed,

        // Files
        documents: uploadedFiles
      };

      // Get company ID for demo mode
      const companyId = isDemoMode ? localStorage.getItem('demo_company_id') || undefined : undefined;

      // Submit the problem
      const response = await submissionService.submitProblem(
        submissionData, 
        companyId,
        isDemoMode
      );

      toast({
        title: 'Problem Submitted Successfully',
        description: isDemoMode 
          ? 'Your demo submission has been saved. In production, this would be sent to our analysts.'
          : 'Your submission has been received. Our Business Analyst team will review it within 5 business days.',
      });

      // Navigate based on mode
      setTimeout(() => {
        if (isDemoMode) {
          navigate('/company/dashboard');
        } else {
          navigate('/company/submissions');
        }
      }, 1500);

    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'Please try again or contact support',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch(currentStep) {
      case 2:
        return formData.contactName && formData.contactEmail && formData.contactPhone;
      case 3:
        return formData.problemTitle && formData.problemCategory && formData.plainDescription;
      case 4:
        return formData.totalAnnualCost && formData.confidenceLevel;
      case 5:
        return formData.industryPrevalence;
      case 6:
        return formData.pilotLikelihood;
      case 10:
        return formData.consentStorage && formData.declarationAgreed;
      default:
        return true;
    }
  };

  const progressPercentage = ((currentStep - 1) / 9) * 100;

  const StepIcon = STEPS.find(s => s.number === currentStep)?.icon || User;

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header with navigation */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="h-auto p-0 text-muted-foreground hover:text-foreground">
            Dashboard
          </Button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">Submit Problem</span>
        </div>

        {/* Header with title and progress */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Submit a Problem</h1>
            <p className="text-muted-foreground mt-1">
              Tell us about the industrial challenge your company is facing
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isDemoMode && (
              <>
                <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                  <Database className="h-3 w-3 mr-1" />
                  Demo Mode
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateDemoProblem}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 gap-1"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate Demo
                </Button>
              </>
            )}
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-sm px-4 py-2">
              <Clock className="h-4 w-4 mr-2" />
              Step {currentStep-1} of 9
            </Badge>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progressPercentage} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Start</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
            <span>Submit</span>
          </div>
        </div>

        {/* Company Profile Summary Card */}
        {companyInfo && (
          <Card className="overflow-hidden border-l-4 border-l-blue-500 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-blue-800">{companyInfo.companyName}</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigate('/company/profile')}
                      className="text-blue-600"
                    >
                      Edit Profile
                    </Button>
                  </div>
                  <p className="text-sm text-blue-700 mt-1">
                    Registration: {companyInfo.registrationNumber} • {companyInfo.industrySectors?.join(', ')} • {companyInfo.location}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Using company profile information from your account
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step Navigation Pills */}
        <div className="flex overflow-x-auto pb-2 gap-2">
          {STEPS.map((step) => (
            <button
              key={step.number}
              onClick={() => {
                if (step.number < currentStep || isStepValid()) {
                  setCurrentStep(step.number);
                }
              }}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                step.number === currentStep
                  ? `bg-${step.color}-100 text-${step.color}-700`
                  : step.number < currentStep
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {getStepIcon(step)}
              <span className="hidden md:inline">{step.title}</span>
              <span className="md:hidden">Step {step.number-1}</span>
            </button>
          ))}
        </div>

        {/* Main Content Card */}
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <CardHeader className="bg-muted/30 py-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg bg-${STEPS.find(s => s.number === currentStep)?.color}-100`}>
                <StepIcon className={`h-6 w-6 text-${STEPS.find(s => s.number === currentStep)?.color}-600`} />
              </div>
              <div>
                <CardTitle className="text-xl">{STEPS.find(s => s.number === currentStep)?.title}</CardTitle>
                <CardDescription>{STEPS.find(s => s.number === currentStep)?.description}</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Step 2: Contact Person */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    The primary contact will be the V&V team's main point of contact during the assessment process.
                  </p>
                </div>

                <h3 className="text-lg font-semibold flex items-center gap-2 text-green-700">
                  <User className="h-5 w-5" />
                  Primary Contact *
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Full Name *</Label>
                    <Input
                      placeholder="e.g., Jane Wanjiku"
                      value={formData.contactName}
                      onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Job Title *</Label>
                    <Input
                      placeholder="e.g., Plant Manager"
                      value={formData.contactTitle}
                      onChange={(e) => setFormData({...formData, contactTitle: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="jane@company.com"
                        value={formData.contactEmail}
                        onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="+254 722 123 456"
                        value={formData.contactPhone}
                        onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Department / Function</Label>
                    <Input
                      placeholder="e.g., Operations, Engineering"
                      value={formData.contactDepartment}
                      onChange={(e) => setFormData({...formData, contactDepartment: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Years in Current Role</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 5"
                      value={formData.yearsInRole}
                      onChange={(e) => setFormData({...formData, yearsInRole: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Decision-Making Authority *</Label>
                  <RadioGroup
                    value={formData.decisionMaker}
                    onValueChange={(value) => setFormData({...formData, decisionMaker: value})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="i-am-decision-maker" id="decision-1" />
                      <Label htmlFor="decision-1">I am the decision-maker for this problem area</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="i-report-to-decision-maker" id="decision-2" />
                      <Label htmlFor="decision-2">I report to the decision-maker</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="submitting-on-behalf" id="decision-3" />
                      <Label htmlFor="decision-3">I am submitting on behalf of a decision-maker</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="external-consultant" id="decision-4" />
                      <Label htmlFor="decision-4">I am an external consultant or advisor</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.decisionMaker === 'submitting-on-behalf' && (
                  <div className="pl-6 space-y-2">
                    <Label className="text-sm font-medium">Decision-maker Name and Title</Label>
                    <Textarea
                      placeholder="Please provide name and title of the decision-maker"
                      value={formData.decisionMakerDetails}
                      onChange={(e) => setFormData({...formData, decisionMakerDetails: e.target.value})}
                      rows={2}
                    />
                  </div>
                )}

                <Separator />

                <h3 className="text-lg font-semibold flex items-center gap-2 text-green-700">
                  <User className="h-5 w-5" />
                  Secondary Contact (Optional)
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Contact Name</Label>
                    <Input
                      placeholder="Full name"
                      value={formData.secondaryName}
                      onChange={(e) => setFormData({...formData, secondaryName: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Contact Title</Label>
                    <Input
                      placeholder="Job title"
                      value={formData.secondaryTitle}
                      onChange={(e) => setFormData({...formData, secondaryTitle: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="secondary@company.com"
                        value={formData.secondaryEmail}
                        onChange={(e) => setFormData({...formData, secondaryEmail: e.target.value})}
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="+254 722 123 456"
                        value={formData.secondaryPhone}
                        onChange={(e) => setFormData({...formData, secondaryPhone: e.target.value})}
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Problem Description */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <Alert className="bg-purple-50 border-purple-200">
                  <Info className="h-4 w-4 text-purple-600" />
                  <AlertDescription className="text-purple-700">
                    Be as specific and data-driven as possible. The more precisely you describe the problem, the faster our team can assess it.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Problem Title *</Label>
                  <Input
                    placeholder="e.g., Undetected boiler steam leaks"
                    value={formData.problemTitle}
                    onChange={(e) => setFormData({...formData, problemTitle: e.target.value})}
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">A short, descriptive name for the problem</p>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Problem Category *</Label>
                  <Select
                    value={formData.problemCategory}
                    onValueChange={(value) => setFormData({...formData, problemCategory: value})}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select problem category" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROBLEM_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {formData.problemCategory === 'other' && (
                    <div className="mt-3">
                      <Input
                        placeholder="Please describe the category"
                        value={formData.otherCategory}
                        onChange={(e) => setFormData({...formData, otherCategory: e.target.value})}
                        className="h-11"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Plain Language Description *</Label>
                  <Textarea
                    placeholder="Describe the problem in 3-5 sentences. What is happening? Where is it happening? Why has it not been solved?"
                    value={formData.plainDescription}
                    onChange={(e) => setFormData({...formData, plainDescription: e.target.value})}
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Root Cause (as understood by your team) *</Label>
                  <Textarea
                    placeholder="What do you believe is causing this problem? It's acceptable to say 'we do not know'"
                    value={formData.rootCause}
                    onChange={(e) => setFormData({...formData, rootCause: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">When did this first appear? *</Label>
                    <Input
                      placeholder="e.g., Since commissioning new boiler in 2019"
                      value={formData.firstAppeared}
                      onChange={(e) => setFormData({...formData, firstAppeared: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Problem Frequency *</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(value) => setFormData({...formData, frequency: value})}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((freq) => (
                          <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.frequency === 'Seasonal' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Specify seasonal months</Label>
                    <Input
                      placeholder="e.g., November-January"
                      value={formData.seasonalMonths}
                      onChange={(e) => setFormData({...formData, seasonalMonths: e.target.value})}
                      className="h-11"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Problem Scope at this Facility *</Label>
                  <Select
                    value={formData.scope}
                    onValueChange={(value) => setFormData({...formData, scope: value})}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCOPE_OPTIONS.map((scope) => (
                        <SelectItem key={scope} value={scope}>{scope}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Previous Attempts to Solve</Label>
                  <Textarea
                    placeholder="Describe any previous solutions tried and why they didn't work"
                    value={formData.previousAttempts}
                    onChange={(e) => setFormData({...formData, previousAttempts: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Financial Impact */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <Alert className="bg-red-50 border-red-200">
                  <DollarSign className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">
                    Grey Costs are the invisible losses that bleed value without appearing on a formal P&L. Please provide your best estimates.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Energy / Utilities (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 1200000"
                      value={formData.costEnergy}
                      onChange={(e) => setFormData({...formData, costEnergy: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Material Waste / Spoilage (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 2500000"
                      value={formData.costMaterial}
                      onChange={(e) => setFormData({...formData, costMaterial: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Machine Downtime (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 3800000"
                      value={formData.costDowntime}
                      onChange={(e) => setFormData({...formData, costDowntime: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Labour / Rework (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 950000"
                      value={formData.costLabour}
                      onChange={(e) => setFormData({...formData, costLabour: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Compliance / Penalties (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 500000"
                      value={formData.costCompliance}
                      onChange={(e) => setFormData({...formData, costCompliance: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Other Costs (KES/year)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 750000"
                      value={formData.costOther}
                      onChange={(e) => setFormData({...formData, costOther: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                {formData.costOther && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Other Cost Description</Label>
                    <Input
                      placeholder="Describe the other costs"
                      value={formData.costOtherDescription}
                      onChange={(e) => setFormData({...formData, costOtherDescription: e.target.value})}
                      className="h-11"
                    />
                  </div>
                )}

                <Separator />

                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold">ESTIMATED TOTAL ANNUAL COST *</Label>
                    <div className="text-2xl font-bold text-red-600">
                      KES {parseInt(formData.totalAnnualCost || '0').toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Supporting Evidence Available</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {EVIDENCE_OPTIONS.map((evidence) => (
                      <div key={evidence} className="flex items-center space-x-2">
                        <Checkbox
                          id={`evidence-${evidence}`}
                          checked={formData.evidence.includes(evidence)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                evidence: [...formData.evidence, evidence]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                evidence: formData.evidence.filter(e => e !== evidence)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`evidence-${evidence}`} className="text-sm">{evidence}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Other Evidence or Context</Label>
                  <Textarea
                    placeholder="Any other evidence you can provide"
                    value={formData.otherEvidence}
                    onChange={(e) => setFormData({...formData, otherEvidence: e.target.value})}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Confidence in Cost Estimate *</Label>
                    <Select
                      value={formData.confidenceLevel}
                      onValueChange={(value) => setFormData({...formData, confidenceLevel: value})}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select confidence level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Rough guess</SelectItem>
                        <SelectItem value="2">2 - Educated estimate</SelectItem>
                        <SelectItem value="3">3 - Reasonably confident</SelectItem>
                        <SelectItem value="4">4 - Very confident</SelectItem>
                        <SelectItem value="5">5 - Precise data-backed figure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">How was this derived?</Label>
                    <Input
                      placeholder="e.g., from utility bills, manager estimate"
                      value={formData.estimateDerivation}
                      onChange={(e) => setFormData({...formData, estimateDerivation: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Market Prevalence */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <Alert className="bg-orange-50 border-orange-200">
                  <TrendingUp className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-700">
                    V&V builds product companies, not custom solutions. For a problem to qualify, it must be systemic — affecting at least 50 comparable facilities.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Industry Prevalence *</Label>
                  <RadioGroup
                    value={formData.industryPrevalence}
                    onValueChange={(value) => setFormData({...formData, industryPrevalence: value})}
                  >
                    {INDUSTRY_PREVALENCE_OPTIONS.map((option) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`prevalence-${option}`} />
                        <Label htmlFor={`prevalence-${option}`}>{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Estimated facilities in Kenya</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 150"
                      value={formData.estimatedKenya}
                      onChange={(e) => setFormData({...formData, estimatedKenya: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Estimated facilities in East Africa</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 450"
                      value={formData.estimatedEastAfrica}
                      onChange={(e) => setFormData({...formData, estimatedEastAfrica: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Affected Industries</Label>
                  <Input
                    placeholder="e.g., all tea factories in the Rift Valley, cold storage operators"
                    value={formData.affectedIndustries}
                    onChange={(e) => setFormData({...formData, affectedIndustries: e.target.value})}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Existing Solutions</Label>
                  <Textarea
                    placeholder="Are there existing solutions in the market? Why haven't they been adopted?"
                    value={formData.existingSolutions}
                    onChange={(e) => setFormData({...formData, existingSolutions: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Sought External Help</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="help-consultant"
                        checked={formData.soughtHelp.includes('consultant')}
                        onCheckedChange={(checked) => {
                          const newHelp = checked 
                            ? [...formData.soughtHelp, 'consultant']
                            : formData.soughtHelp.filter(h => h !== 'consultant');
                          setFormData({...formData, soughtHelp: newHelp});
                        }}
                      />
                      <Label htmlFor="help-consultant">Engaged a consultant (did not solve it)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="help-equipment"
                        checked={formData.soughtHelp.includes('equipment')}
                        onCheckedChange={(checked) => {
                          const newHelp = checked 
                            ? [...formData.soughtHelp, 'equipment']
                            : formData.soughtHelp.filter(h => h !== 'equipment');
                          setFormData({...formData, soughtHelp: newHelp});
                        }}
                      />
                      <Label htmlFor="help-equipment">Purchased equipment (did not solve it)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="help-internal"
                        checked={formData.soughtHelp.includes('internal')}
                        onCheckedChange={(checked) => {
                          const newHelp = checked 
                            ? [...formData.soughtHelp, 'internal']
                            : formData.soughtHelp.filter(h => h !== 'internal');
                          setFormData({...formData, soughtHelp: newHelp});
                        }}
                      />
                      <Label htmlFor="help-internal">Tried an internal improvement project</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="help-none"
                        checked={formData.soughtHelp.includes('none')}
                        onCheckedChange={(checked) => {
                          const newHelp = checked 
                            ? [...formData.soughtHelp, 'none']
                            : formData.soughtHelp.filter(h => h !== 'none');
                          setFormData({...formData, soughtHelp: newHelp});
                        }}
                      />
                      <Label htmlFor="help-none">No — we have accepted this as a cost of doing business</Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Pilot Readiness */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <Alert className="bg-indigo-50 border-indigo-200">
                  <Briefcase className="h-4 w-4 text-indigo-600" />
                  <AlertDescription className="text-indigo-700">
                    If accepted, you may be asked to pilot the squad's solution. This helps us understand your readiness.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Pilot Likelihood *</Label>
                  <RadioGroup
                    value={formData.pilotLikelihood}
                    onValueChange={(value) => setFormData({...formData, pilotLikelihood: value})}
                  >
                    {PILOT_LIKELIHOOD_OPTIONS.map((option) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`pilot-${option}`} />
                        <Label htmlFor={`pilot-${option}`}>{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Approval Authority</Label>
                  <Select
                    value={formData.approvalAuthority}
                    onValueChange={(value) => setFormData({...formData, approvalAuthority: value})}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Who needs to approve a pilot?" />
                    </SelectTrigger>
                    <SelectContent>
                      {APPROVAL_AUTHORITY_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Approval Timeline</Label>
                    <Input
                      placeholder="e.g., 30 days"
                      value={formData.approvalTimeline}
                      onChange={(e) => setFormData({...formData, approvalTimeline: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Preferred Pilot Duration</Label>
                    <Input
                      placeholder="e.g., 60 days"
                      value={formData.pilotDuration}
                      onChange={(e) => setFormData({...formData, pilotDuration: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Success Criteria</Label>
                  <Textarea
                    placeholder="What would a successful solution need to achieve?"
                    value={formData.successCriteria}
                    onChange={(e) => setFormData({...formData, successCriteria: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Budget Range (KES/year)</Label>
                    <Select
                      value={formData.budgetRange}
                      onValueChange={(value) => setFormData({...formData, budgetRange: value})}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Below KES 1M">Below KES 1M</SelectItem>
                        <SelectItem value="KES 1M - 5M">KES 1M - 5M</SelectItem>
                        <SelectItem value="KES 5M - 10M">KES 5M - 10M</SelectItem>
                        <SelectItem value="KES 10M - 25M">KES 10M - 25M</SelectItem>
                        <SelectItem value="Above KES 25M">Above KES 25M</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Commercial Model</Label>
                    <Select
                      value={formData.commercialModel}
                      onValueChange={(value) => setFormData({...formData, commercialModel: value})}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select preferred model" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMERCIAL_MODEL_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Pilot Access</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="access-area"
                        checked={formData.pilotAccess.includes('area')}
                        onCheckedChange={(checked) => {
                          const newAccess = checked 
                            ? [...formData.pilotAccess, 'area']
                            : formData.pilotAccess.filter(a => a !== 'area');
                          setFormData({...formData, pilotAccess: newAccess});
                        }}
                      />
                      <Label htmlFor="access-area">Access to affected production area</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="access-data"
                        checked={formData.pilotAccess.includes('data')}
                        onCheckedChange={(checked) => {
                          const newAccess = checked 
                            ? [...formData.pilotAccess, 'data']
                            : formData.pilotAccess.filter(a => a !== 'data');
                          setFormData({...formData, pilotAccess: newAccess});
                        }}
                      />
                      <Label htmlFor="access-data">Access to operational data</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="access-liaison"
                        checked={formData.pilotAccess.includes('liaison')}
                        onCheckedChange={(checked) => {
                          const newAccess = checked 
                            ? [...formData.pilotAccess, 'liaison']
                            : formData.pilotAccess.filter(a => a !== 'liaison');
                          setFormData({...formData, pilotAccess: newAccess});
                        }}
                      />
                      <Label htmlFor="access-liaison">Named internal liaison for the squad</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="access-install"
                        checked={formData.pilotAccess.includes('install')}
                        onCheckedChange={(checked) => {
                          const newAccess = checked 
                            ? [...formData.pilotAccess, 'install']
                            : formData.pilotAccess.filter(a => a !== 'install');
                          setFormData({...formData, pilotAccess: newAccess});
                        }}
                      />
                      <Label htmlFor="access-install">Access for equipment installation</Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 7: Technical Environment */}
            {currentStep === 7 && (
              <div className="space-y-6">
                <Alert className="bg-pink-50 border-pink-200">
                  <Cpu className="h-4 w-4 text-pink-600" />
                  <AlertDescription className="text-pink-700">
                    This helps us understand the environment where any solution would be deployed.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Facility Age (years)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 25"
                      value={formData.facilityAge}
                      onChange={(e) => setFormData({...formData, facilityAge: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Facility Size (sq m)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 15000"
                      value={formData.facilitySize}
                      onChange={(e) => setFormData({...formData, facilitySize: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Operating Hours</Label>
                    <Input
                      placeholder="e.g., 24/7, 2 shifts"
                      value={formData.operatingHours}
                      onChange={(e) => setFormData({...formData, operatingHours: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Operating Days/Year</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 350"
                      value={formData.operatingDays}
                      onChange={(e) => setFormData({...formData, operatingDays: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Monitoring Systems</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {['SCADA', 'ERP', 'MES', 'Basic PLCs', 'Manual records', 'IoT sensors', 'Energy management', 'No formal systems'].map((system) => (
                      <div key={system} className="flex items-center space-x-2">
                        <Checkbox
                          id={`system-${system}`}
                          checked={formData.monitoringSystems.includes(system)}
                          onCheckedChange={(checked) => {
                            const newSystems = checked 
                              ? [...formData.monitoringSystems, system]
                              : formData.monitoringSystems.filter(s => s !== system);
                            setFormData({...formData, monitoringSystems: newSystems});
                          }}
                        />
                        <Label htmlFor={`system-${system}`}>{system}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Connectivity</Label>
                  <Select
                    value={formData.connectivity}
                    onValueChange={(value) => setFormData({...formData, connectivity: value})}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select connectivity type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONNECTIVITY_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Primary Equipment</Label>
                  <Textarea
                    placeholder="Describe the key equipment involved"
                    value={formData.primaryEquipment}
                    onChange={(e) => setFormData({...formData, primaryEquipment: e.target.value})}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Equipment Brand</Label>
                    <Input
                      placeholder="e.g., Siemens, ABB"
                      value={formData.equipmentBrand}
                      onChange={(e) => setFormData({...formData, equipmentBrand: e.target.value})}
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Equipment Age (years)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 10"
                      value={formData.equipmentAge}
                      onChange={(e) => setFormData({...formData, equipmentAge: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Technical Constraints</Label>
                  <Textarea
                    placeholder="Any technical restrictions the squad should be aware of?"
                    value={formData.technicalConstraints}
                    onChange={(e) => setFormData({...formData, technicalConstraints: e.target.value})}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Step 8: Regulatory Context */}
            {currentStep === 8 && (
              <div className="space-y-6">
                <Alert className="bg-teal-50 border-teal-200">
                  <Shield className="h-4 w-4 text-teal-600" />
                  <AlertDescription className="text-teal-700">
                    Understanding the regulatory landscape helps us design compliant solutions.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Regulatory Related</Label>
                  <RadioGroup
                    value={formData.regulatoryRelated}
                    onValueChange={(value) => setFormData({...formData, regulatoryRelated: value})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes-penalties" id="reg-yes1" />
                      <Label htmlFor="reg-yes1">Yes — we face regulatory penalties</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes-anticipated" id="reg-yes2" />
                      <Label htmlFor="reg-yes2">Yes — we anticipate new regulations</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="reg-no" />
                      <Label htmlFor="reg-no">No — purely operational</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="unsure" id="reg-unsure" />
                      <Label htmlFor="reg-unsure">Not sure</Label>
                    </div>
                  </RadioGroup>
                </div>

                {(formData.regulatoryRelated === 'yes-penalties' || formData.regulatoryRelated === 'yes-anticipated') && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Regulatory Bodies</Label>
                    <Input
                      placeholder="e.g., NEMA, KEBS, ERC"
                      value={formData.regulatoryBodies}
                      onChange={(e) => setFormData({...formData, regulatoryBodies: e.target.value})}
                      className="h-11"
                    />
                  </div>
                )}

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Certifications</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {['ISO 9001', 'ISO 14001', 'ISO 50001', 'ISO 22000', 'HACCP', 'KEBS', 'Global GAP', 'Fair Trade'].map((cert) => (
                      <div key={cert} className="flex items-center space-x-2">
                        <Checkbox
                          id={`cert-${cert}`}
                          checked={formData.certifications.includes(cert)}
                          onCheckedChange={(checked) => {
                            const newCerts = checked 
                              ? [...formData.certifications, cert]
                              : formData.certifications.filter(c => c !== cert);
                            setFormData({...formData, certifications: newCerts});
                          }}
                        />
                        <Label htmlFor={`cert-${cert}`}>{cert}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Upcoming Deadlines</Label>
                  <Textarea
                    placeholder="Any audits, inspections, or compliance deadlines?"
                    value={formData.upcomingDeadlines}
                    onChange={(e) => setFormData({...formData, upcomingDeadlines: e.target.value})}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Step 9: Self-Assessment */}
            {currentStep === 9 && (
              <div className="space-y-6">
                <Alert className="bg-cyan-50 border-cyan-200">
                  <Scale className="h-4 w-4 text-cyan-600" />
                  <AlertDescription className="text-cyan-700">
                    Rate your problem on a scale of 1-5. This helps us prioritise submissions.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Financial Severity</Label>
                      <span className="text-sm font-medium">{formData.severityRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.severityRating}
                      onChange={(e) => setFormData({...formData, severityRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Minor irritant</span>
                      <span>Critically expensive</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Urgency</Label>
                      <span className="text-sm font-medium">{formData.urgencyRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.urgencyRating}
                      onChange={(e) => setFormData({...formData, urgencyRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Can wait years</span>
                      <span>Must be solved now</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Industry Prevalence</Label>
                      <span className="text-sm font-medium">{formData.prevalenceRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.prevalenceRating}
                      onChange={(e) => setFormData({...formData, prevalenceRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Unique to us</span>
                      <span>Every facility faces it</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Data Availability</Label>
                      <span className="text-sm font-medium">{formData.dataAvailabilityRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.dataAvailabilityRating}
                      onChange={(e) => setFormData({...formData, dataAvailabilityRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Very little data</span>
                      <span>Comprehensive data</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Openness to Tech</Label>
                      <span className="text-sm font-medium">{formData.techOpennessRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.techOpennessRating}
                      onChange={(e) => setFormData({...formData, techOpennessRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Prefer traditional</span>
                      <span>Fully open to tech</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Management Support</Label>
                      <span className="text-sm font-medium">{formData.managementSupportRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.managementSupportRating}
                      onChange={(e) => setFormData({...formData, managementSupportRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Low priority</span>
                      <span>Full executive backing</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Decision Speed</Label>
                      <span className="text-sm font-medium">{formData.decisionSpeedRating}/5</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={formData.decisionSpeedRating}
                      onChange={(e) => setFormData({...formData, decisionSpeedRating: e.target.value})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Very slow (12+ months)</span>
                      <span>Fast (within 30 days)</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Additional Context</Label>
                    <Textarea
                      placeholder="Anything else we should know?"
                      value={formData.additionalContext}
                      onChange={(e) => setFormData({...formData, additionalContext: e.target.value})}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 10: Consent & Submit */}
            {currentStep === 10 && (
              <div className="space-y-6">
                <Alert className="bg-emerald-50 border-emerald-200">
                  <FileSignature className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-700">
                    Please review and consent to the following terms.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold">Data Processing Consent</h3>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consent-storage"
                        checked={formData.consentStorage}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, consentStorage: checked as boolean})
                        }
                      />
                      <Label htmlFor="consent-storage">
                        V&V storing and processing the information in this form in its V&V Portal system *
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consent-contact"
                        checked={formData.consentContact}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, consentContact: checked as boolean})
                        }
                      />
                      <Label htmlFor="consent-contact">
                        V&V's Business Analyst team contacting me to schedule a Grey Cost Audit
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consent-anonymised"
                        checked={formData.consentAnonymised}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, consentAnonymised: checked as boolean})
                        }
                      />
                      <Label htmlFor="consent-anonymised">
                        V&V sharing anonymised data in its portfolio reporting
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consent-updates"
                        checked={formData.consentUpdates}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, consentUpdates: checked as boolean})
                        }
                      />
                      <Label htmlFor="consent-updates">
                        V&V contacting me with updates about the Problem Bank programme
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="consent-squad"
                        checked={formData.consentSquad}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, consentSquad: checked as boolean})
                        }
                      />
                      <Label htmlFor="consent-squad">
                        V&V assigning a Forge squad to research and develop a solution
                      </Label>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h3 className="font-semibold">Data Sharing Level</h3>
                    <RadioGroup
                      value={formData.dataSharingLevel}
                      onValueChange={(value) => setFormData({...formData, dataSharingLevel: value})}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="anonymised-only" id="share-1" />
                        <Label htmlFor="share-1">Anonymised data only (for initial research)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="identified-after-nda" id="share-2" />
                        <Label htmlFor="share-2">Identified data after NDA with assigned squad</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no-sharing" id="share-3" />
                        <Label htmlFor="share-3">Do not share any data at this stage</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h3 className="font-semibold">Declaration</h3>
                    <div className="bg-muted/30 p-4 rounded-lg text-sm">
                      <p className="mb-2">I, the undersigned, confirm that:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        <li>I am authorised to submit this form on behalf of the company</li>
                        <li>The information provided is accurate and complete to the best of my knowledge</li>
                        <li>Submitting this form does not create any obligation on V&V to accept the problem</li>
                        <li>Submitting this form does not create any obligation on my company to proceed with any pilot</li>
                        <li>I have read and understood V&V's Data Protection and Privacy Policy</li>
                      </ul>
                    </div>

                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="declaration"
                        checked={formData.declarationAgreed}
                        onCheckedChange={(checked) => 
                          setFormData({...formData, declarationAgreed: checked as boolean})
                        }
                      />
                      <Label htmlFor="declaration" className="font-medium">
                        I agree to the declaration above *
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* File Upload Section - Common across all steps */}
            <div className="mt-6">
              <Label className="text-sm font-medium">Upload Supporting Documents</Label>
              <div className="mt-2 border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm font-medium">Click to upload or drag and drop</span>
                  <span className="text-xs text-muted-foreground">
                    PDF, Excel, Images, Word (max 10MB each)
                  </span>
                </label>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>

          <CardFooter className="border-t bg-muted/20 px-6 py-4">
            <div className="flex justify-between w-full">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 2}
                className="gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>

              <div className="text-sm text-muted-foreground">
                Step {currentStep-1} of 9
              </div>

              {currentStep < 10 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isStepValid()}
                  className="bg-green-600 hover:bg-green-700 gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Submit Problem
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}